import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Info, RefreshCw, XCircle } from 'lucide-react'
import api from '../../services/api'
import { SettlementNoticeList } from '../SettlementNoticeBanner'
import ApiChannelCard from './ApiChannelCard'
import BalanceCard from './BalanceCard'
import ClicksChannelCard from './ClicksChannelCard'
import DailyEarningsChart from './DailyEarningsChart'
import DailyValueChart from './DailyValueChart'
import IntakeRejections from './IntakeRejections'
import IntegrationStatus from './IntegrationStatus'
import KpiRow from './KpiRow'
import NetBalance from './NetBalance'
import PendingTermsCard from './PendingTermsCard'
import Purchases from './Purchases'
import WorkspaceHeader from './WorkspaceHeader'
import WorkspaceSidebar from './WorkspaceSidebar'
import { downloadDashboardCsv } from './exportCsv'
import { dashboardParams, PARTNER_DASHBOARD_RESPONSIVE_CSS, workspaceSections } from './model'

// ════════════════════════════════════════════════════════════════════════════
//   PARTNER WORKSPACE - the affiliate landing view.
//
//   One range, one request: everything below comes out of a single
//   GET /partner/dashboard, so no block can be cut to a different period
//   than its neighbours, and the balance, the cards and the tables come from
//   one database snapshot. The backend scopes it through effective_company(),
//   which is also what makes "Viewing as ..." show the impersonated partner
//   with nothing to do here.
//
//   Every section is rendered on presence: the backend leaves out the keys a
//   company doesn't have (a channel, purchases, a balance), so a partner
//   never sees a block of zeros for something they don't do.
//
//   A partner under a split agreement (Partners Edge) gets no net balance -
//   their balance is the advance card, as before - and their money lines
//   stay the split ones (KpiRow, DailyValueChart). Two balances built
//   differently would not agree, and a partner must never see that.
//
//   Test leads are never in here: the request doesn't ask for them, and the
//   backend leaves them out of the workspace and the CSV regardless.
// ════════════════════════════════════════════════════════════════════════════
export default function PartnerOverview({ range, onRangeChange, partnerName, onOpenCampaigns, onOpenBuyer }) {
  const params = useMemo(() => dashboardParams(range), [range])
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)
  const [attempt, setAttempt] = useState(0)

  useEffect(() => {
    let cancelled = false
    setLoading(true)
    setError(false)
    api.get('/partner/dashboard', { params })
      .then(({ data: d }) => { if (!cancelled) setData(d) })
      .catch(() => { if (!cancelled) setError(true) })
      .finally(() => { if (!cancelled) setLoading(false) })
    // A slow answer for a range the partner has already moved off must not
    // land on top of the one they're looking at.
    return () => { cancelled = true }
  }, [params, attempt])

  const retry = useCallback(() => setAttempt((n) => n + 1), [])
  const exportCsv = useCallback(() => downloadDashboardCsv(params, data?.range), [params, data])

  return (
    <div className="pw-layout">
      <style>{PARTNER_DASHBOARD_RESPONSIVE_CSS}</style>
      <WorkspaceSidebar
        sections={workspaceSections(data)}
        partnerName={partnerName}
        onOpenCampaigns={onOpenCampaigns}
        onOpenBuyer={onOpenBuyer}
      />
      <div className="pw-main">
        <WorkspaceHeader
          workspace={data?.workspace}
          range={range}
          resolvedRange={data?.range}
          onRangeChange={onRangeChange}
          onExport={exportCsv}
        />
        {loading && !data && <OverviewSkeleton />}
        {error && <OverviewError onRetry={retry} />}
        {!error && data && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16, opacity: loading ? 0.55 : 1, transition: 'opacity .15s' }} aria-busy={loading}>
            <WorkspaceBody data={data} onOpenBuyer={onOpenBuyer} />
          </div>
        )}
      </div>
    </div>
  )
}

function WorkspaceBody({ data, onOpenBuyer }) {
  const ws = data.workspace || {}
  const channels = ws.channels || {}
  const integration = ws.integration || {}
  const underAgreement = !!data.split
  // Under an agreement the channel figure is the whole lead value, of which
  // the partner is paid their share - KpiRow above says how much.
  const earningsLabel = underAgreement ? 'Lead value' : 'Your earnings'

  return (
    <>
      {ws.net_balance && <NetBalance net={ws.net_balance} previous={ws.previous_balances || []} />}
      {underAgreement && (
        <section id="pw-statement" aria-label="Your balance" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {data.balance && <BalanceCard balance={data.balance} split={data.split} />}
          <KpiRow kpis={data.kpis} split={data.split} />
          {data.pending_terms_block && <PendingTermsCard block={data.pending_terms_block} />}
        </section>
      )}

      {(channels.api || channels.clicks) && (
        <div className={channels.api && channels.clicks ? 'pw-channels' : undefined}>
          {channels.api && (
            <ApiChannelCard block={channels.api} live={integration.api_live} earningsLabel={earningsLabel} />
          )}
          {channels.clicks && (
            <ClicksChannelCard block={channels.clicks} link={integration.tracking_link} earningsLabel={earningsLabel} />
          )}
        </div>
      )}

      {ws.intake && <IntakeRejections intake={ws.intake} />}

      {underAgreement
        ? <DailyValueChart series={data.series} pendingBlock={data.pending_terms_block} />
        : <DailyEarningsChart daily={ws.daily} channels={channels} />}

      <div className={ws.purchases ? 'pw-bottom' : undefined}>
        {ws.purchases && <Purchases purchases={ws.purchases} daily={ws.daily} onOpenBuyer={onOpenBuyer} />}
        <IntegrationStatus integration={integration} channels={channels} />
      </div>

      <SettlementNoticeList notices={data.notices} />

      {data.unpriced && (
        <div role="note" style={{
          display: 'flex', alignItems: 'center', gap: 8, fontSize: 12,
          color: 'var(--text-muted)', padding: '6px 2px',
        }}>
          <Info size={13} />
          {Number(data.unpriced.leads_sold_without_value || 0).toLocaleString('en-US')} sold leads have no value set (staff only)
        </div>
      )}
    </>
  )
}

function OverviewSkeleton() {
  const bar = (h, w = '100%') => <div className="pd-skeleton" style={{ height: h, width: w }} />
  return (
    <div aria-busy="true" aria-label="Loading dashboard" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div className="pw-card" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {bar(12, '30%')}{bar(36, '40%')}{bar(10)}
      </div>
      <div className="pw-channels">
        {[0, 1].map((i) => (
          <div key={i} className="pw-card" style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {bar(10, '30%')}{bar(22, '60%')}{bar(8)}{bar(8, '70%')}{bar(8, '40%')}
          </div>
        ))}
      </div>
      <div className="pw-card">{bar(220)}</div>
    </div>
  )
}

function OverviewError({ onRetry }) {
  return (
    <div className="pw-card" role="alert" style={{
      padding: 36, textAlign: 'center', display: 'flex',
      flexDirection: 'column', alignItems: 'center', gap: 10,
    }}>
      <XCircle size={26} style={{ color: 'var(--red)', opacity: 0.7 }} />
      <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--text-primary)' }}>
        Couldn't load your dashboard
      </span>
      <button type="button" className="btn btn-ghost btn-sm" onClick={onRetry}>
        <RefreshCw size={12} /> Retry
      </button>
    </div>
  )
}
